/**
 * DropdownList Component - Migrated from chariot-ui
 *
 * Migration reason: Need to add Select All/Deselect All functionality for FilterDropdown
 * and avoid dependency on legacy shared component library.
 *
 * Original: chariot-ui/DropdownList
 * Migrated: 2025-01-20
 *
 * Changes from original:
 * - Added onSelectAll and onDeselectAll props
 * - Implemented tri-state "Select All" checkbox at top of options list
 * - Checkbox states: checked (all selected), unchecked (none selected), indeterminate (some selected)
 * - "Select All" option has bold text and separator line for visual distinction
 * - Removed legacy button pattern (Select All | Deselect All text buttons)
 * - All other functionality remains the same
 */

import {
  ChevronDownIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Minus } from 'lucide-react';
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';

import { Checkbox } from '@/components/chariot-ui';
import { SearchBar } from '@/components/legacy-ui/SearchBar';
import { Button } from '@/components/ui/button';

export type Severity =
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | 'info'
  | 'exposure';

export interface DropdownOption {
  id?: string | number;
  severity?: Severity;
  label: string;
  count?: number | string;
  value?: string | number;
  icon?: React.ReactNode;
  onClick?: () => void;
  isDivider?: boolean;
  disabled?: boolean;
  checked?: boolean;
  isGroup?: boolean;
  items?: DropdownOption[];
  isExpanded?: boolean;
  groupId?: string;
  groupItems?: DropdownOption[];
  tooltip?: string;
  isSelectAll?: boolean; // Special flag for "Select All" option
}

// Constants for virtualization
const ROW_HEIGHT = 50;
const MAX_DROPDOWN_HEIGHT = 600;
const VIEWPORT_BUFFER = 40;

interface DropdownListProps {
  border?: boolean;
  options: DropdownOption[];
  onSelect?: (option: DropdownOption) => void;
  onToggleCheckbox?: (option: DropdownOption) => void;
  className?: string;
  style?: React.CSSProperties;
  virtualized?: boolean;
  title?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onSearchKeyDown?: (e: React.KeyboardEvent) => void;
  searchPlaceholder?: string;
  multiSelect?: boolean;
  onApply?: () => void;
  isPraetorianUser?: boolean;
  fitWidth?: boolean;
  isLoadingOptions?: boolean;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
}

export const DropdownList: React.FC<DropdownListProps> = ({
  border = true,
  options,
  onSelect,
  onToggleCheckbox,
  className,
  style,
  virtualized = false,
  title,
  searchValue,
  onSearchChange,
  onSearchKeyDown,
  searchPlaceholder = 'Search...',
  multiSelect = false,
  onApply,
  isPraetorianUser = false,
  fitWidth = false,
  isLoadingOptions = false,
  onSelectAll,
  onDeselectAll,
}) => {
  const [maxHeight, setMaxHeight] = useState(MAX_DROPDOWN_HEIGHT);
  const contentRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  // Live region announcement for screen readers when single match found
  const [screenReaderAnnouncement, setScreenReaderAnnouncement] = useState('');

  /**
   * Calculate tri-state for "Select All" checkbox
   * - All selected: checked = true
   * - None selected: checked = false
   * - Some selected: indeterminate = true
   */
  const selectAllState = useMemo(() => {
    if (!multiSelect || (!onSelectAll && !onDeselectAll)) {
      return null;
    }

    // Get all selectable options (non-disabled, non-group)
    const selectableOptions = options.filter(
      opt => !opt.disabled && !opt.isGroup
    );

    if (selectableOptions.length === 0) {
      return { checked: false, indeterminate: false };
    }

    const selectedCount = selectableOptions.filter(opt => opt.checked).length;

    if (selectedCount === 0) {
      return { checked: false, indeterminate: false };
    } else if (selectedCount === selectableOptions.length) {
      return { checked: true, indeterminate: false };
    } else {
      return { checked: false, indeterminate: true };
    }
  }, [options, multiSelect, onSelectAll, onDeselectAll]);

  /**
   * Enhanced options with "Select All" injected as first option
   * Only included when multiSelect is enabled and callbacks are provided
   */
  const enhancedOptions = useMemo(() => {
    if (!multiSelect || !selectAllState) {
      return options;
    }

    const selectAllOption: DropdownOption = {
      id: '__select_all__',
      label: 'Select All',
      value: '__select_all__',
      checked: selectAllState.checked,
      // Custom flag to identify this as the Select All option
      isSelectAll: true,
    };

    return [selectAllOption, ...options];
  }, [options, multiSelect, selectAllState]);

  /**
   * Announce to screen readers when search results in exactly one match
   * Informs users they can press Enter to select
   */
  useEffect(() => {
    if (!searchValue || !multiSelect) {
      setScreenReaderAnnouncement('');
      return;
    }

    // Count selectable options (exclude groups, disabled, AND "Select All")
    const selectableOptions = enhancedOptions
      .flatMap(opt => {
        if (
          (
            opt as DropdownOption & {
              isGroup?: boolean;
              items?: DropdownOption[];
            }
          ).isGroup
        ) {
          return (
            (opt as DropdownOption & { items?: DropdownOption[] }).items || []
          );
        }
        return [opt];
      })
      .filter(opt => {
        // Exclude disabled options
        if (opt.disabled) return false;

        // Exclude groups
        if ((opt as DropdownOption & { isGroup?: boolean }).isGroup)
          return false;

        // Exclude "Select All" option - CHECK MULTIPLE WAYS
        if (opt.label === 'Select All') return false;
        if (opt.value === '__select_all__') return false;
        if ((opt as DropdownOption & { isSelectAll?: boolean }).isSelectAll)
          return false;

        return true;
      });

    if (selectableOptions.length === 1) {
      setScreenReaderAnnouncement(
        `One option found: ${selectableOptions[0].label}. Press Enter to select.`
      );
    } else if (selectableOptions.length === 0) {
      setScreenReaderAnnouncement('No options found');
    } else {
      setScreenReaderAnnouncement('');
    }
  }, [searchValue, enhancedOptions, multiSelect]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Optimize virtualizer configuration
  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: enhancedOptions.length,
    getScrollElement: () => contentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
    paddingStart: 4,
    paddingEnd: 4,
  });

  // Calculate available space for the dropdown
  useLayoutEffect(() => {
    const calculateMaxHeight = (): void => {
      if (!parentRef.current) return;

      const windowHeight = window.innerHeight;
      const dropdownTop = parentRef.current.getBoundingClientRect().top;
      const maxAllowedHeight = windowHeight - dropdownTop - VIEWPORT_BUFFER;
      setMaxHeight(
        Math.max(0, Math.min(MAX_DROPDOWN_HEIGHT, maxAllowedHeight))
      );
    };

    calculateMaxHeight();
    window.addEventListener('resize', calculateMaxHeight);
    return () => {
      window.removeEventListener('resize', calculateMaxHeight);
    };
  }, []);

  const cloneIconWithClasses = (
    icon: React.ReactNode,
    disabled?: boolean
  ): React.ReactNode => {
    if (!React.isValidElement(icon)) return icon;

    return React.cloneElement(
      icon as React.ReactElement<{ className?: string }>,
      {
        className: twMerge(
          'size-5 inline-flex justify-center items-center mr-1.5',
          disabled
            ? 'text-chariot-text-disabled'
            : 'text-chariot-text-secondary'
        ),
      }
    );
  };

  const renderOption = (
    option: DropdownOption,
    index: number,
    hideBorder?: boolean
  ): React.JSX.Element => {
    if (option.isDivider) {
      return <div className="border-chariot-stroke my-1 h-px border-t" />;
    }

    const isExpanded =
      option.isGroup &&
      expandedGroups.has(option.id?.toString() ?? option.label);
    const canExpandGroup =
      option.isGroup && (isPraetorianUser || Boolean(option.items?.length));

    // Special handling for "Select All" option
    const isSelectAllOption = option.isSelectAll === true;
    const showIndeterminate =
      isSelectAllOption && selectAllState?.indeterminate;

    return (
      <div
        className={twMerge(
          'w-full pb-1',
          border && !hideBorder && 'border-chariot-stroke border-b',
          // Add separator after "Select All" option
          isSelectAllOption && 'border-chariot-stroke border-b-2'
        )}
      >
        <div
          className={twMerge(
            'hover:bg-chariot-background-secondary flex items-center gap-3 rounded-md px-2 py-2',
            option.disabled &&
              'hover:bg-chariot-background-primary cursor-default'
          )}
          title={option.tooltip ?? undefined}
        >
          {multiSelect ? (
            <div className="shrink-0 items-center">
              <button
                onClick={e => {
                  e.stopPropagation();
                  // Handle "Select All" click
                  if (isSelectAllOption) {
                    if (
                      selectAllState?.checked ||
                      selectAllState?.indeterminate
                    ) {
                      onDeselectAll?.();
                    } else {
                      onSelectAll?.();
                    }
                  } else {
                    onToggleCheckbox?.(option);
                  }
                }}
                className="flex items-center justify-center"
                aria-checked={option.checked}
                role="checkbox"
              >
                {showIndeterminate ? (
                  // Render indeterminate state with minus icon
                  <div
                    role="checkbox"
                    aria-checked="mixed"
                    className="mr-0 flex size-6 items-center p-1"
                  >
                    <div
                      className={twMerge(
                        'size-4 rounded-[2px] border-2',
                        'flex items-center justify-center',
                        'bg-chariot-brand-primary border-chariot-brand-primary'
                      )}
                    >
                      <Minus className="size-3 stroke-[3px] text-white" />
                    </div>
                  </div>
                ) : (
                  <Checkbox
                    isChecked={option.checked ?? false}
                    disabled={option.disabled}
                  />
                )}
              </button>
            </div>
          ) : (
            option.severity && (
              <div
                className={twMerge(
                  'size-4 shrink-0 rounded-xs border',
                  `bg-chariot-status-${option.severity}`,
                  `border-chariot-stroke-${option.severity}`
                )}
              />
            )
          )}

          <div
            className={twMerge(
              'flex min-w-0 flex-1 items-center justify-between gap-4',
              !option.disabled && 'cursor-pointer'
            )}
            onClick={e => {
              e.stopPropagation();
              if (!option.disabled) {
                if (multiSelect) {
                  // Handle "Select All" click
                  if (isSelectAllOption) {
                    if (
                      selectAllState?.checked ||
                      selectAllState?.indeterminate
                    ) {
                      onDeselectAll?.();
                    } else {
                      onSelectAll?.();
                    }
                  } else {
                    onToggleCheckbox?.(option);
                  }
                } else {
                  option.onClick?.();
                  onSelect?.(option);
                }
              }
            }}
          >
            <div
              className={twMerge(
                'flex min-w-0 flex-1 items-center font-medium',
                option.disabled
                  ? 'text-chariot-text-disabled'
                  : 'text-chariot-text-primary',
                // Make "Select All" bold and slightly different
                isSelectAllOption && 'font-bold'
              )}
            >
              {option.icon &&
                cloneIconWithClasses(option.icon, option.disabled)}
              <span className="truncate">{option.label}</span>
            </div>
            <div className="flex items-center gap-2">
              {option.count !== undefined && (
                <span className="text-chariot-text-primary shrink-0 text-base">
                  {option.count.toLocaleString()}
                </span>
              )}
              {option.tooltip && (
                <InformationCircleIcon className="text-chariot-text-secondary size-4 shrink-0" />
              )}
              {canExpandGroup && (
                <button
                  onClick={e => {
                    e.stopPropagation();
                    toggleGroup(option.id?.toString() ?? option.label);
                  }}
                  className="hover:bg-chariot-background-secondary shrink-0 rounded p-1"
                >
                  <ChevronDownIcon
                    className={twMerge(
                      'text-chariot-text-secondary size-4 transition-transform duration-200',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </button>
              )}
            </div>
          </div>
        </div>
        {canExpandGroup && isExpanded && option.items && (
          <div className="mt-1 ml-2 space-y-1">
            {option.items.map((item, itemIndex) => (
              <div key={item.id ?? itemIndex}>
                {renderOption(item, itemIndex, true)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Calculate minimum height
  const getMinHeight = (): number => {
    let minHeight = 0;
    if (title) minHeight += 40;
    if (onSearchChange !== undefined) minHeight += 60;
    if (multiSelect) minHeight += 70;
    return minHeight;
  };

  // Structure for virtualized content
  const renderVirtualizedContent = () => (
    <div ref={contentRef} className="flex-1 overflow-auto px-4 py-2">
      {isLoadingOptions ? (
        <p className="text-chariot-text-secondary min-h-36 py-2 text-center text-sm">
          Loading options...
        </p>
      ) : enhancedOptions.length === 0 ? (
        <p className="text-chariot-text-secondary min-h-36 py-2 text-center text-sm">
          No results found
        </p>
      ) : (
        <div
          className="relative w-full"
          style={{
            height: `${virtualizer.getTotalSize().toString()}px`,
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map(virtualRow => (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              className="absolute top-0 left-0 w-full"
              style={{
                height: `${virtualRow.size.toString()}px`,
                transform: `translateY(${virtualRow.start.toString()}px)`,
              }}
            >
              {renderOption(
                enhancedOptions[virtualRow.index],
                virtualRow.index
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Structure for non-virtualized content
  const renderStandardContent = () => (
    <div className="flex-1 overflow-auto px-4 py-2">
      {isLoadingOptions ? (
        <p className="text-chariot-text-secondary min-h-36 py-2 text-center text-sm">
          Loading options...
        </p>
      ) : enhancedOptions.length === 0 ? (
        <p className="text-chariot-text-secondary min-h-36 py-2 text-center text-sm">
          No results found
        </p>
      ) : (
        <div className="flex flex-col gap-1">
          {enhancedOptions.map((option, index) => (
            <div key={option.id?.toString() ?? option.label}>
              {renderOption(option, index)}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div
      ref={parentRef}
      className={twMerge(
        'animate-in fade-in duration-200',
        fitWidth ? 'w-fit' : virtualized ? 'w-[280px]' : 'w-[400px]',
        className
      )}
      style={{
        ...style,
        display: 'flex',
        flexDirection: 'column',
        // Always set maxHeight to ensure Apply button stays visible
        maxHeight: maxHeight,
        height: virtualized
          ? Math.max(
              getMinHeight(),
              Math.min(virtualizer.getTotalSize() + 140, maxHeight)
            )
          : 'auto', // Changed from undefined to 'auto' for proper flexbox behavior
        minHeight: getMinHeight(),
      }}
      data-testid="dropdown-list"
    >
      {/* Header */}
      <div className="flex-none">
        {title && (
          <div className="px-4 pt-4">
            <h3 className="text-chariot-text-secondary text-xs">{title}</h3>
          </div>
        )}

        {onSearchChange && (
          <div className="px-4 pt-2">
            <SearchBar
              value={searchValue}
              onChange={onSearchChange}
              onKeyDown={onSearchKeyDown}
              placeholder={searchPlaceholder}
              size="sm"
            />
            {/* Screen reader announcement for single match */}
            <div
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className="sr-only"
            >
              {screenReaderAnnouncement}
            </div>
          </div>
        )}
      </div>

      {/* Content - conditionally render based on virtualization flag */}
      {virtualized ? renderVirtualizedContent() : renderStandardContent()}

      {/* Apply button */}
      {multiSelect && (
        <div className="border-chariot-stroke mt-auto flex-none border-t">
          <div className="px-4 py-3">
            <Button onClick={() => onApply?.()} className="w-full">
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
